import { useRef, useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { useProjectDetail } from '@/hooks/project/useProjectDetail';
import ProjectMembers from '../ProjectMembers';
import ProjectHeader from './ProjectHeader';
import ConversationSection from './ConversationSection';
import FilesSection from './FilesSection';
import ProjectModals from './ProjectModals';
import UploadProgressModal from './UploadProgressModal';
import ShareModal from '../ShareModal';
import CreateGroupChatModal from '../CreateGroupChatModal';
import FilePreview from '../FilePreview';
import MobileHeader from '../MobileHeader';

interface ProjectDetailProps {
  projectId: string;
  projectName: string;
  projectRole?: string; // 🔥 用户在项目中的角色 ('owner' | 'manager' | 'editor' | 'viewer')
  projectDescribe: string;
  projectInstructions?: string;
  projectStarred?: boolean;
  projectArchived?: boolean;
  isUploadingFiles?: boolean;
  onFileUpload: (files: File[], targetPath?: string) => void;
  onShowProjectFiles: () => void;
  onConversationSelect?: (conversationId: string, projectId: string) => void;
  onSwitchToChat?: (
    conversationId: string,
    projectId: string,
    messageDetails?: {
      content: string;
      isRetry?: boolean;
      options?: {
        usePublic?: boolean;
        allProjects?: boolean;
        thinkType?: 'think' | 'no-think' | 'hard-think';
        clientId?: string | null;
        pendingId?: string; // 🔥 架构简化：移到 options 中，可选
      };
    }
  ) => void;
  usePublic?: boolean;
  allProjects?: boolean;
  onBackToProjects?: () => void;
  onRenameProject?: (projectId: string, newName: string) => void;
  // onDeleteProject?: (projectId: string) => void;
  onUpdateProject?: (project: {
    id: string;
    name: string;
    description: string;
    instructions: string;
  }) => void;
  onStarProject?: () => Promise<void>;
  onArchiveProject?: (projectId: string, archived: boolean) => Promise<void>;
}

export default function ProjectDetail({
  projectId,
  projectName,
  projectRole,
  projectDescribe,
  projectInstructions = '',
  projectStarred = false,
  projectArchived = false,
  isUploadingFiles = false,
  onFileUpload,
  onConversationSelect,
  onSwitchToChat,
  onBackToProjects,
  onUpdateProject,
  onStarProject,
  onArchiveProject,
}: ProjectDetailProps) {
  const { t } = useTranslation();

  // Hook to force re-render on language change
  const useForceUpdate = () => {
    const [, setForceUpdate] = useState(0);
    useState(0);
    useEffect(() => {
      const onLanguageChanged = () => setForceUpdate(n => n + 1);
      i18nInstance.on('languageChanged', onLanguageChanged);
      return () => i18nInstance.off('languageChanged', onLanguageChanged);
    }, []);
  };
  useForceUpdate();

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 🔥 计算权限变量
  const isViewer = projectRole === 'viewer';
  // 🔥 只有 owner 和 manager 可以管理项目（编辑项目信息、归档、Instructions等）
  // 🔥 archived 项目不能被管理
  const canManageProject =
    (projectRole === 'owner' || projectRole === 'manager') && !projectArchived;
  // 🔥 archived 项目权限同 viewer，禁用编辑和文件操作，不能进入 conversation 详情页
  const isReadOnlyProject = isViewer || projectArchived;
  const [showCreateGroupChatModal, setShowCreateGroupChatModal] =
    useState(false);

  const hook = useProjectDetail({
    projectId,
    projectName,
    projectInstructions,
    projectStarred,
    projectArchived,
    onFileUpload,
    onConversationSelect,
    onSwitchToChat,
    onStarProject,
    onArchiveProject,
    onUpdateProject,
  });

  // Show members management if requested
  if (hook.showMembers) {
    return (
      <ProjectMembers
        projectId={projectId}
        projectName={projectName}
        projectArchived={projectArchived}
        onBack={() => hook.setShowMembers(false)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Mobile Header */}
      <MobileHeader>
        <h3 className="text-base font-bold truncate">{projectName}</h3>
      </MobileHeader>
      <div className="flex flex-1 min-h-0 w-full overflow-hidden">
        {/* 左侧主内容 */}
        <div className="flex-1 flex flex-col bg-claude-bg h-full overflow-hidden pt-4 sm:pt-6 xl:pt-12 px-4 sm:px-6 xl:px-8">
          {/* Main Content */}
          <div className="flex-1 min-h-0 overflow-y-auto xl:overflow-hidden pb-6 sm:pb-8">
            <div className="w-full max-w-7xl mx-auto xl:h-full min-h-0">
              {/* Two Column Layout */}
              <div className="flex flex-col xl:flex-row gap-4 lg:gap-6 xl:gap-12 min-h-0 xl:h-full">
                {/* Left Column - Title, Chat Input & Conversations */}
                <div className="flex-1 min-w-[14rem] flex flex-col min-h-0 xl:h-full xl:max-w-3xl">
                  <ProjectHeader
                    projectName={projectName}
                    projectDescribe={projectDescribe}
                    projectRole={projectRole}
                    canManageProject={canManageProject}
                    isStarred={hook.isStarred}
                    isStarring={hook.isStarring}
                    isArchived={hook.isArchived}
                    isArchiving={hook.isArchiving}
                    onBackToProjects={onBackToProjects}
                    onToggleStar={hook.handleToggleStar}
                    onToggleArchive={hook.handleToggleArchive}
                    onEditProject={() => hook.setIsEditProjectModalOpen(true)}
                    onShowMembers={() => hook.setShowMembers(true)}
                    onCreateGroupChat={() => setShowCreateGroupChatModal(true)}
                  />

                  <ConversationSection
                    projectId={projectId}
                    projectName={projectName}
                    isViewer={isReadOnlyProject}
                    projectArchived={projectArchived}
                    onNewChat={hook.handleNewChat}
                    conversations={hook.conversations}
                    visibleConversations={hook.visibleConversations}
                    filteredConversations={hook.filteredConversations}
                    isLoading={hook.isLoading}
                    searchQuery={hook.searchQuery}
                    selectedConversationIds={hook.selectedConversationIds}
                    hasMoreConversations={hook.hasMoreConversations}
                    onConversationSelect={
                      // 🔥 archived 项目不能进入 conversation 详情页
                      projectArchived ? () => {} : hook.handleConversationSelect
                    }
                    onToggleSelect={hook.toggleConversationSelect}
                    onMenuAction={hook.handleMenuAction}
                    onBatchSelect={hook.handleBatchSelect}
                    onShowMore={hook.handleShowMoreConversations}
                    formatDate={hook.formatConversationDate}
                    onBulkDelete={hook.handleBulkDeleteConversations}
                    onRemoveFromProject={hook.handleRemoveFromProject}
                    onSelectAll={() => {
                      const visibleIds = hook.visibleConversations.map(
                        c => c.conversationId
                      );
                      const allVisibleSelected =
                        visibleIds.length > 0 &&
                        visibleIds.every(id =>
                          hook.selectedConversationIds.has(id)
                        );
                      hook.setSelectedConversationIds(prev => {
                        const next = new Set(prev);
                        if (allVisibleSelected) {
                          visibleIds.forEach(id => next.delete(id));
                        } else {
                          visibleIds.forEach(id => next.add(id));
                        }
                        return next;
                      });
                    }}
                  />
                </div>

                {/* Right Column - Instructions & Files */}
                <div className="w-full xl:w-[420px] 2xl:w-[480px] flex-shrink-0 flex flex-col min-h-0 xl:h-full border border-gray-200 rounded-2xl p-3 sm:p-4 overflow-hidden">
                  {/* Instructions Section */}
                  <div className="mb-4 sm:mb-6 border-b border-gray-300 pb-3 sm:pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-medium text-gray-900">
                        {t('project.instructions')}
                      </h3>
                      {/* 🔥 只有 owner/manager 可以编辑 Instructions */}
                      <button
                        onClick={() => {
                          const currentInstructions = hook.instructions || '';
                          hook.setTempInstructions(currentInstructions);
                          hook.setOriginalInstructions(currentInstructions);
                          hook.setIsInstructionsModalOpen(true);
                        }}
                        disabled={!canManageProject}
                        className={`p-1 rounded ${
                          !canManageProject
                            ? 'cursor-not-allowed opacity-50 text-gray-300'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                        title={
                          !canManageProject
                            ? t(
                                'project.onlyOwnersAndManagersCanEditInstructions'
                              )
                            : undefined
                        }
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">
                      {t('project.addInstructionsToTailor')}
                    </p>
                  </div>

                  {/* Files Section */}
                  <FilesSection
                    projectId={projectId}
                    isViewer={isReadOnlyProject}
                    currentNodes={hook.currentNodes}
                    currentPath={hook.currentPath}
                    isLoadingFiles={hook.isLoadingFiles}
                    fileSearchQuery={hook.fileSearchQuery}
                    setFileSearchQuery={hook.setFileSearchQuery}
                    selectedFileIds={hook.selectedFileIds}
                    hoveredFileId={hook.hoveredFileId}
                    setHoveredFileId={hook.setHoveredFileId}
                    allFilesSelected={hook.allFilesSelected}
                    totalSelectableFiles={hook.totalSelectableFiles}
                    toggleFileSelect={hook.toggleFileSelect}
                    clearFileSelection={hook.clearFileSelection}
                    handleSelectAllFiles={hook.handleSelectAllFiles}
                    isUploadingFiles={isUploadingFiles}
                    isUploadingToFolder={
                      hook.fileUploadHook.isUploadingToFolder
                    }
                    isDraggingOver={hook.fileUploadHook.isDraggingOver}
                    isCreatingFolder={
                      hook.folderOperationsHook.isCreatingFolder
                    }
                    isMaxDepth={hook.isMaxDepth}
                    handleDragOver={hook.fileUploadHook.handleDragOver}
                    handleDragLeave={hook.fileUploadHook.handleDragLeave}
                    handleDrop={hook.fileUploadHook.handleDrop}
                    triggerFileUpload={hook.triggerFileUpload}
                    onCreateFolder={() => {
                      hook.folderOperationsHook.setNewFolderName('');
                      hook.folderOperationsHook.setIsCreateFolderModalOpen(
                        true
                      );
                    }}
                    onAddTextContent={hook.openAddTextContentModal}
                    handleFolderClick={hook.handleFolderClick}
                    handleBreadcrumbClick={hook.handleBreadcrumbClick}
                    handleOpenMoveModal={
                      hook.folderOperationsHook.handleOpenMoveModal
                    }
                    handleBatchDelete={
                      hook.folderOperationsHook.handleBatchDelete
                    }
                    handleBatchDownload={
                      hook.folderOperationsHook.handleBatchDownload
                    }
                    handleDownloadFile={
                      hook.folderOperationsHook.handleDownloadFile
                    }
                    handleDeleteFile={
                      hook.folderOperationsHook.handleDeleteFile
                    }
                    handleOpenEditFolder={
                      hook.folderOperationsHook.handleOpenEditFolder
                    }
                    openFilePreview={hook.openFilePreview}
                    filesContainerRef={hook.filesContainerRef}
                    fileItemRefs={hook.fileItemRefs}
                    isSelectingFiles={hook.filesBoxSelection.isSelecting}
                    filesSelectionBoxStyle={
                      hook.filesBoxSelection.selectionBoxStyle
                    }
                    handleFilesMouseDown={
                      hook.filesBoxSelection.handleMouseDown
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Upload Progress Modal with Overlay */}
          <UploadProgressModal
            isUploading={hook.fileUploadHook.isUploading}
            uploadProgress={hook.fileUploadHook.uploadProgress}
            isCompressing={hook.fileUploadHook.isCompressing}
            compressionProgress={hook.fileUploadHook.compressionProgress}
            compressionDetail={hook.fileUploadHook.compressionDetail}
          />

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={hook.handleFileSelect}
            className="hidden"
            accept="*"
          />

          {/* Hidden Upload Input for Folder */}
          <input
            ref={hook.fileUploadHook.uploadInputRef}
            type="file"
            multiple
            onChange={hook.fileUploadHook.handleUploadFiles}
            className="hidden"
            accept="*"
          />

          {/* All Modals */}
          <ProjectModals
            projectId={projectId}
            projectName={projectName}
            projectDescribe={projectDescribe}
            // Instructions modal
            isInstructionsModalOpen={hook.isInstructionsModalOpen}
            setIsInstructionsModalOpen={hook.setIsInstructionsModalOpen}
            tempInstructions={hook.tempInstructions}
            setTempInstructions={hook.setTempInstructions}
            onSaveInstructions={hook.handleSaveInstructions}
            isSavingInstructions={hook.isSavingInstructions}
            // Create folder modal
            isCreateFolderModalOpen={
              hook.folderOperationsHook.isCreateFolderModalOpen
            }
            setIsCreateFolderModalOpen={
              hook.folderOperationsHook.setIsCreateFolderModalOpen
            }
            newFolderName={hook.folderOperationsHook.newFolderName}
            setNewFolderName={hook.folderOperationsHook.setNewFolderName}
            newFolderNameValidation={
              hook.folderOperationsHook.newFolderNameValidation
            }
            onCreateFolder={hook.folderOperationsHook.handleCreateFolder}
            isCreatingFolder={hook.folderOperationsHook.isCreatingFolder}
            // Edit folder modal
            isEditFolderModalOpen={
              hook.folderOperationsHook.isEditFolderModalOpen
            }
            setIsEditFolderModalOpen={
              hook.folderOperationsHook.setIsEditFolderModalOpen
            }
            editFolderName={hook.folderOperationsHook.editFolderName}
            setEditFolderName={hook.folderOperationsHook.setEditFolderName}
            editFolderNameValidation={
              hook.folderOperationsHook.editFolderNameValidation
            }
            editingFolder={hook.folderOperationsHook.editingFolder}
            setEditingFolder={hook.folderOperationsHook.setEditingFolder}
            onEditFolder={hook.folderOperationsHook.handleEditFolder}
            isEditingFolder={hook.folderOperationsHook.isEditingFolder}
            // Move modal
            isMoveModalOpen={hook.folderOperationsHook.isMoveModalOpen}
            setIsMoveModalOpen={hook.folderOperationsHook.setIsMoveModalOpen}
            moveModalPath={hook.folderOperationsHook.moveModalPath}
            setMoveModalPath={hook.folderOperationsHook.setMoveModalPath}
            moveModalCurrentNodes={
              hook.folderOperationsHook.moveModalCurrentNodes
            }
            moveTargetPath={hook.folderOperationsHook.moveTargetPath}
            onMoveSelectFolder={
              hook.folderOperationsHook.handleMoveSelectFolder
            }
            onMoveConfirm={hook.folderOperationsHook.handleMoveConfirm}
            isMoving={hook.folderOperationsHook.isMoving}
            // File/Folder confirm dialog
            fileConfirmDialogProps={
              hook.folderOperationsHook.fileConfirmDialogProps
            }
            // Edit project modal
            isEditProjectModalOpen={hook.isEditProjectModalOpen}
            setIsEditProjectModalOpen={hook.setIsEditProjectModalOpen}
            instructions={hook.instructions}
            setInstructions={hook.setInstructions}
            onUpdateProject={hook.onUpdateProject}
            // Rename conversation modal
            showConversationRenameModal={hook.showConversationRenameModal}
            setShowConversationRenameModal={hook.setShowConversationRenameModal}
            renameValue={hook.renameValue}
            setRenameValue={hook.setRenameValue}
            onRenameConversation={hook.handleRenameConversation}
            isRenamingConversation={hook.isRenamingConversation}
            setRenameConversationId={() => {}}
            // Conversation confirm dialog
            conversationDialogProps={hook.conversationDialogProps}
            // File conflict modal
            isConflictModalOpen={hook.fileUploadHook.isConflictModalOpen}
            conflictFiles={hook.fileUploadHook.conflictFiles}
            isCheckingConflicts={hook.fileUploadHook.isCheckingConflicts}
            onConflictCancel={hook.fileUploadHook.handleConflictCancel}
            onConflictSkip={hook.fileUploadHook.handleConflictSkip}
            onConflictOverwrite={hook.fileUploadHook.handleConflictOverwrite}
            // Add text content modal
            isAddTextContentModalOpen={hook.isAddTextContentModalOpen}
            setIsAddTextContentModalOpen={hook.setIsAddTextContentModalOpen}
            addTextContentTitle={hook.addTextContentTitle}
            setAddTextContentTitle={hook.setAddTextContentTitle}
            addTextContentContent={hook.addTextContentContent}
            setAddTextContentContent={hook.setAddTextContentContent}
            onAddTextContent={hook.handleAddTextContent}
            isAddingTextContent={hook.isAddingTextContent}
          />

          {/* Share Modal */}
          <ShareModal
            isOpen={hook.shareModal.isOpen}
            onClose={() =>
              hook.setShareModal({
                isOpen: false,
                conversationId: '',
                conversationTitle: '',
              })
            }
            conversationId={hook.shareModal.conversationId}
            conversationTitle={hook.shareModal.conversationTitle}
            projectId={projectId}
          />

          {/* Create Group Chat Modal */}
          <CreateGroupChatModal
            isOpen={showCreateGroupChatModal}
            onClose={() => setShowCreateGroupChatModal(false)}
            projectId={projectId}
            projectName={projectName}
            onGroupCreated={async conversationId => {
              setShowCreateGroupChatModal(false);
              // Add the new group chat ID to the set immediately to prevent Loading... entry
              if (!window.groupChatIds) {
                window.groupChatIds = new Set();
              }
              window.groupChatIds.add(conversationId);
              // Refresh Group chats list
              if (window.refreshSidebarGroupChats) {
                await window.refreshSidebarGroupChats();
              }
              // Navigate directly to the group chat via hash router
              // Don't use onConversationSelect as it adds to Recent chats
              window.location.hash = `#/chat/${conversationId}`;
            }}
          />
        </div>

        {/* 右侧文件预览 (分屏模式) - 像 ArtifactViewer 一样 */}
        {/* 🔥 viewer 角色禁用文件编辑 */}
        {hook.previewFile && hook.previewFileUrl && (
          <FilePreview
            fileUrl={hook.previewFileUrl}
            filename={hook.previewFile.name}
            onClose={hook.closeFilePreview}
            projectId={projectId}
            filePath={hook.previewFile.path || hook.previewFile.name}
            mode="split"
            readOnly={isReadOnlyProject}
          />
        )}
      </div>
    </div>
  );
}
